import { ref, computed } from 'vue'
import { logger, getErrorMessage } from '@siclop/kstone-vue'

export interface LoadingStep {
  id: string
  label: string
  status: 'pending' | 'loading' | 'completed' | 'error'
  error?: string
}

// Global state for app loading
const steps = ref<LoadingStep[]>([])
const isInitialized = ref(false)
const hasError = ref(false)

/**
 * Composable for managing app startup loading with progress tracking.
 * Shows a loading screen with progress bar while stores and menus load.
 */
export function useAppLoading() {
  /**
   * Initialize loading steps
   */
  function initSteps(stepDefinitions: { id: string; label: string }[]) {
    steps.value = stepDefinitions.map(s => ({
      id: s.id,
      label: s.label,
      status: 'pending'
    }))
    isInitialized.value = false
    hasError.value = false
  }

  /**
   * Mark a step as loading
   */
  function startStep(stepId: string) {
    const step = steps.value.find(s => s.id === stepId)
    if (step) {
      step.status = 'loading'
    }
  }

  /**
   * Mark a step as completed
   */
  function completeStep(stepId: string) {
    const step = steps.value.find(s => s.id === stepId)
    if (step) {
      step.status = 'completed'
    }
  }

  /**
   * Mark a step as failed
   */
  function failStep(stepId: string, error?: string) {
    const step = steps.value.find(s => s.id === stepId)
    if (step) {
      step.status = 'error'
      step.error = error
      hasError.value = true
    }
  }

  /**
   * Execute a loading step with automatic status tracking
   */
  async function executeStep(stepId: string, fn: () => Promise<void>) {
    startStep(stepId)
    try {
      await fn()
      completeStep(stepId)
    } catch (e) {
      logger.error(`[useAppLoading] Step ${stepId} failed:`, e)
      failStep(stepId, getErrorMessage(e, 'Erreur inconnue'))
      // Don't throw - allow other steps to continue
    }
  }

  /**
   * Mark initialization as complete
   */
  function finishLoading() {
    isInitialized.value = true
  }

  /**
   * Reset loading state (for re-authentication)
   */
  function reset() {
    steps.value = []
    isInitialized.value = false
    hasError.value = false
  }

  // Computed properties
  const completedSteps = computed(() =>
    steps.value.filter(s => s.status === 'completed').length
  )

  const totalSteps = computed(() => steps.value.length)

  const progress = computed(() =>
    totalSteps.value > 0
      ? Math.round((completedSteps.value / totalSteps.value) * 100)
      : 0
  )

  const currentStep = computed(() =>
    steps.value.find(s => s.status === 'loading')
  )

  const isLoading = computed(() =>
    steps.value.length > 0 && !isInitialized.value
  )

  const allCompleted = computed(() =>
    steps.value.length > 0 && steps.value.every(s => s.status === 'completed' || s.status === 'error')
  )

  return {
    steps,
    isInitialized,
    hasError,
    isLoading,
    progress,
    currentStep,
    completedSteps,
    totalSteps,
    allCompleted,
    initSteps,
    startStep,
    completeStep,
    failStep,
    executeStep,
    finishLoading,
    reset
  }
}
